import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import { ActionPermissions, CustomPermission } from '../../../@types';
import { Link } from '../../../components/atoms/Link';
import { isUserHasPermission } from '../../../configs/permissions';
import { STANDARD_FIELD_HEIGHT } from '../../../configs/ui-constants';

export interface PartListHeaderProps {
  total: number;
  permissions: CustomPermission[] | undefined;
  exportAnchor: HTMLElement | null;
  onExportClick: (event: React.MouseEvent<HTMLElement>) => void;
  onExportClose: () => void;
  onExportCsv: () => void;
  onExportExcel: () => void;
  /** Callback to open the column-picker dialog. When omitted, the Columns
   *  button is hidden — used for the client/customer curated view. */
  onColumnsClick?: () => void;
  /** Callback to open the saved-reports modal. */
  onReportsClick: () => void;
}

export const PartListHeader: React.FC<PartListHeaderProps> = ({
  total,
  permissions,
  exportAnchor,
  onExportClick,
  onExportClose,
  onExportCsv,
  onExportExcel,
  onColumnsClick,
  onReportsClick,
}) => {
  const theme = useTheme();
  const white = theme.palette.common.white;

  const toolbarButtonSx = useMemo(
    () => ({
      textTransform: 'none' as const,
      fontSize: '12px',
      color: white,
      borderColor: alpha(white, 0.3),
      bgcolor: 'transparent',
      py: 0.25,
      height: STANDARD_FIELD_HEIGHT,
      minHeight: STANDARD_FIELD_HEIGHT,
      '&:hover': {
        borderColor: white,
        bgcolor: alpha(white, 0.08),
      },
    }),
    [white]
  );

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'v4.sidebar',
        color: white,
        px: '20px',
        py: '12px',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: white }}>Parts</Typography>
        {total > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              color: alpha(white, 0.85),
              fontWeight: 400,
              bgcolor: alpha(white, 0.12),
              px: 1,
              py: '2px',
              borderRadius: '10px',
            }}
          >
            {total.toLocaleString()} results
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {isUserHasPermission(ActionPermissions.Report_My_Report_Search, permissions) && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<SummarizeOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onReportsClick}
            sx={toolbarButtonSx}
          >
            Reports
          </MuiButton>
        )}
        {onColumnsClick && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onColumnsClick}
            sx={toolbarButtonSx}
          >
            Columns
          </MuiButton>
        )}
        {isUserHasPermission(ActionPermissions.Part_Export, permissions) && (
          <>
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={onExportClick}
              sx={toolbarButtonSx}
            >
              Export
            </MuiButton>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={onExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={onExportCsv}>Export CSV</MenuItem>
              <MenuItem onClick={onExportExcel}>Export Excel</MenuItem>
            </Menu>
          </>
        )}
        {isUserHasPermission(ActionPermissions.Service_Create, permissions) && (
          <Link href="/parts/createRepair">
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              sx={toolbarButtonSx}
            >
              Create Repair
            </MuiButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};
